import { codeFont, numFont } from '../../utils/fonts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useServerSearch } from '../../hooks/useServerSearch';
import { useUserStore } from '../../store';
import { usePartCodeOnly } from '../../utils/partSearchPrefs';
import { DEFAULT_VAT_RATE, calcPriceInclVat, formatPriceWithCurrency } from '../../utils/vat';
import { formatQuantity } from '../../utils';
import { canScanBarcodes } from '../../utils/barcodeDetector';
import BarcodeScannerDialog from '../maintenances/BarcodeScannerDialog';
import CatalogueStockReconcileDialog from '../maintenances/CatalogueStockReconcileDialog';

/**
 * Reusable rich part picker (registry search, same data as the maintenance
 * add-part dialog): name + code, sell price and live stock in the dropdown.
 *
 * Barcode-first: a USB/bluetooth scan gun types the code and fires Enter faster
 * than the 300 ms search debounce — Enter runs an immediate exact lookup and
 * adds a single hit straight to the caller. The camera scanner button uses the
 * same resolution path (capability-gated, not org-flagged).
 *
 * With `sourceToggle`, a "vain varastonhallinnassa olevat" checkbox scopes the
 * search: checked (default, persisted) → inventory-managed parts only ("selling
 * off the shelf"); unchecked → all registry parts PLUS vendor-catalogue matches
 * folded into the same dropdown (same plumbing as the work-order AddPartDialog).
 * A picked catalogue row is materialized into a Part and delivered through
 * onSelect(part, { fromCatalogue: true }) so callers can react (e.g. the lähete
 * pre-flips its special-order toggle).
 *
 * `catalogueFilters` adds the vendor/catalogue filter chips (multi-select; any
 * selected → registry hidden, only those catalogues searched, empty query
 * browses them); with `filtersCollapsible` the chip row hides behind a "Näytä
 * suodattimet" link until opened (an active selection always shows), so the
 * till and the lähete stay uncluttered. `stockMatchCheck` runs the stock-match pre-check on catalogue
 * picks and opens the reconcile dialog when the workshop already stocks the
 * code (same flow as the work-order AddPartDialog). `shelfOnlyDefault` /
 * `persistKey` control the checkbox default and its localStorage persistence
 * (persistKey null → not persisted). Opening the dropdown on an empty field
 * lists the newest parts (same as the work-order dialog); `loadOnMount` also
 * preloads them on mount.
 *
 * `jobsToggle` + `onSelectJob` (Kassamyynti): a second "Hae myös töistä"
 * checkbox folds the org's saved jobs (/api/jobs/) into the same dropdown;
 * a picked job is delivered through onSelectJob(job) untouched. Default OFF,
 * persisted per org under `jobsPersistKey`. Jobs never take part in barcode
 * scanning; the scan-gun Enter path does consider them when the toggle is on.
 */
function PartAutocomplete({
  onSelect,
  label,
  placeholder,
  size = 'small',
  autoFocus = false,
  clearOnSelect = true,
  disabled = false,
  big = false,
  sourceToggle = false,
  catalogueFilters = false,
  filtersCollapsible = false,
  shelfOnlyDefault = true,
  persistKey = 'partsSalesShelfOnly',
  loadOnMount = false,
  stockMatchCheck = false,
  inputTestId = 'part-autocomplete-input',
  jobsToggle = false,
  onSelectJob = null,
  jobsPersistKey = 'partsSalesSearchJobs',
}) {
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'warning' });
  const [reconcile, setReconcile] = useState(null);
  const inputRef = useRef(null);

  const inventoryEnabled = currentOrganization?.inventory_tracking_enabled;
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const scannerEnabled = canScanBarcodes();
  const orgId = currentOrganization?.organization_id;
  const toggleVisible = sourceToggle && !!inventoryEnabled;
  const catalogueEnabled = sourceToggle && (currentOrganization?.vendor_catalogues_enabled ?? false);

  const [shelfOnly, setShelfOnly] = useState(() => {
    if (!sourceToggle || !inventoryEnabled) return false;
    if (!persistKey) return shelfOnlyDefault;
    const stored = window.localStorage.getItem(`${persistKey}:${orgId}`);
    return stored == null ? shelfOnlyDefault : stored === 'true';
  });

  // Saved-job search: opt-in per host, default OFF, persisted per org. Storage
  // access is guarded — a blocked localStorage must never crash the till.
  const jobsToggleVisible = !!jobsToggle && typeof onSelectJob === 'function';
  const [searchJobs, setSearchJobs] = useState(() => {
    if (!jobsToggleVisible) return false;
    try {
      return window.localStorage.getItem(`${jobsPersistKey}:${orgId}`) === 'true';
    } catch {
      return false;
    }
  });
  const jobsActive = jobsToggleVisible && searchJobs;
  const [jobResults, setJobResults] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const jobDebounceRef = useRef(null);
  const jobSeqRef = useRef(0);

  // Vendor/catalogue filter chips: any selected → only those catalogues are
  // searched and the registry is hidden (same semantics as AddPartDialog).
  const chipsEnabled = catalogueFilters && catalogueEnabled;
  const [catalogueSources, setCatalogueSources] = useState([]);
  const [selectedCatalogueIds, setSelectedCatalogueIds] = useState([]);
  const chipsActive = chipsEnabled && selectedCatalogueIds.length > 0;
  // Collapsible hosts start folded; a selected chip keeps the row visible regardless.
  const [filtersOpen, setFiltersOpen] = useState(!filtersCollapsible);
  const chipsVisible = chipsEnabled && catalogueSources.length > 0 && (filtersOpen || chipsActive);

  useEffect(() => {
    if (!chipsEnabled) return;
    // Best-effort: chips just don't render if the sources fetch fails.
    axios
      .get('/api/catalogue-search/sources/')
      .then((res) => setCatalogueSources(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCatalogueSources([]));
  }, [chipsEnabled]);

  // Two catalogues from the same vendor would render identical chip labels —
  // fall back to the catalogue name for those.
  const duplicateVendorLabels = useMemo(() => {
    const counts = {};
    catalogueSources.forEach((s) => {
      const label = s.vendor_name || s.name;
      counts[label] = (counts[label] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter((label) => counts[label] > 1));
  }, [catalogueSources]);

  const shelfActive = toggleVisible && shelfOnly && !chipsActive;
  const catalogueActive = catalogueEnabled && !shelfActive;

  // Code-only search is a workshop-wide preference, shared with the work-order
  // dialog and the Osat/Varasto lists (see utils/partSearchPrefs).
  const [codeOnly, setCodeOnly] = usePartCodeOnly();

  const extraParams = useMemo(
    () => ({ ...(shelfActive ? { has_inventory: true } : {}), ...(codeOnly ? { code_only: true } : {}) }),
    [shelfActive, codeOnly]
  );
  const { items: parts, autocompleteProps, setSearchInput, loadInitial } = useServerSearch('/api/parts/', {
    minSearchLength: 3,
    loadOnMount,
    extraParams,
  });

  // Vendor-catalogue search runs in parallel with the registry search on the
  // same input; failures are non-fatal (registry results still render).
  const [catalogueResults, setCatalogueResults] = useState([]);
  const [catalogueBusy, setCatalogueBusy] = useState(false);
  const catalogueDebounceRef = useRef(null);

  const runCatalogueSearch = useCallback(
    (query) => {
      if (catalogueDebounceRef.current) clearTimeout(catalogueDebounceRef.current);
      const q = (query || '').trim();
      // With filter chips active an empty/short query browses those catalogues;
      // otherwise the backend resolver needs >= 3 chars (trigram index).
      if (!catalogueActive || (q.length < 3 && !chipsActive)) {
        setCatalogueResults([]);
        return;
      }
      catalogueDebounceRef.current = setTimeout(async () => {
        try {
          const params = { q };
          if (chipsActive) params.catalogues = selectedCatalogueIds.join(',');
          if (codeOnly) params.code_only = true;
          const res = await axios.get('/api/catalogue-search/', { params });
          setCatalogueResults(res.data?.results || []);
        } catch {
          setCatalogueResults([]);
        }
      }, 300);
    },
    [catalogueActive, chipsActive, selectedCatalogueIds, codeOnly]
  );

  // Saved-job search runs in parallel with the registry search on the same
  // input (same 300 ms debounce, same >= 3 char threshold). A sequence counter
  // drops stale responses so a slow earlier query never overwrites a newer one.
  const runJobSearch = useCallback(
    (query) => {
      if (jobDebounceRef.current) clearTimeout(jobDebounceRef.current);
      const q = (query || '').trim();
      if (!jobsActive || q.length < 3) {
        setJobResults([]);
        return;
      }
      jobDebounceRef.current = setTimeout(async () => {
        const seq = ++jobSeqRef.current;
        setJobsLoading(true);
        try {
          // /api/jobs/ searches name, description and job_code (no code_only scope).
          const res = await axios.get('/api/jobs/', { params: { search: q, typeahead: 'true' } });
          if (seq !== jobSeqRef.current) return;
          const data = res.data?.results || res.data;
          setJobResults(Array.isArray(data) ? data : []);
        } catch {
          if (seq === jobSeqRef.current) setJobResults([]);
        } finally {
          if (seq === jobSeqRef.current) setJobsLoading(false);
        }
      }, 300);
    },
    [jobsActive]
  );

  // Re-run (or clear) the job search when the toggle flips. Note MUI's default
  // clearOnBlur empties the field when the checkbox takes focus, so in practice
  // this clears stale hits on untick and the seller retypes after ticking.
  const prevJobsActiveRef = useRef(jobsActive);
  useEffect(() => {
    if (prevJobsActiveRef.current === jobsActive) return;
    prevJobsActiveRef.current = jobsActive;
    runJobSearch(inputValue);
  }, [jobsActive, inputValue, runJobSearch]);

  const toggleJobs = (checked) => {
    setSearchJobs(checked);
    try {
      window.localStorage.setItem(`${jobsPersistKey}:${orgId}`, String(checked));
    } catch {
      /* storage blocked — the toggle still works for this session */
    }
  };

  // Re-run the catalogue search when the chip selection changes, so toggling a
  // vendor immediately re-scopes the visible results.
  const prevChipsRef = useRef(selectedCatalogueIds);
  useEffect(() => {
    if (prevChipsRef.current === selectedCatalogueIds) return;
    prevChipsRef.current = selectedCatalogueIds;
    runCatalogueSearch(inputValue);
  }, [selectedCatalogueIds, inputValue, runCatalogueSearch]);

  // Re-run the registry search when the scope flips — the debounced search
  // closure only picks up the new has_inventory / code_only params on the next render.
  // A short/empty field reloads the first page so the browse list never shows the old scope.
  const prevScopeRef = useRef(`${shelfActive}|${codeOnly}`);
  useEffect(() => {
    const scope = `${shelfActive}|${codeOnly}`;
    if (prevScopeRef.current === scope) return;
    prevScopeRef.current = scope;
    const q = (inputValue || '').trim();
    if (q.length >= 3) setSearchInput(q);
    else loadInitial();
  }, [shelfActive, codeOnly, inputValue, setSearchInput, loadInitial]);

  // The catalogue half is a separate request; re-scope it on the same flip.
  const prevCodeOnlyRef = useRef(codeOnly);
  useEffect(() => {
    if (prevCodeOnlyRef.current === codeOnly) return;
    prevCodeOnlyRef.current = codeOnly;
    runCatalogueSearch(inputValue);
  }, [codeOnly, inputValue, runCatalogueSearch]);

  const toggleShelf = (checked) => {
    setShelfOnly(checked);
    if (persistKey) window.localStorage.setItem(`${persistKey}:${orgId}`, String(checked));
    if (checked) {
      setCatalogueResults([]);
    } else {
      runCatalogueSearch(inputValue);
    }
  };

  // Catalogue rows after registry parts, suppressing rows already linked to a
  // shown registry Part (same dedup rule as the work-order AddPartDialog).
  // Filter chips active → only the selected catalogues' rows (registry hidden).
  // Saved jobs (when the toggle is on) come last, tagged `__job` with a synthetic
  // id that cannot collide with numeric part ids or `__cat_` catalogue rows.
  const options = useMemo(() => {
    let base;
    if (!catalogueActive || chipsActive || catalogueResults.length === 0) {
      base = chipsActive
        ? catalogueResults.map((c) => ({ ...c, __catalogue: true, id: `__cat_${c.item_id}` }))
        : parts || [];
    } else {
      const linked = new Set(
        (parts || [])
          .filter((p) => p.source_catalogue)
          .map((p) => `${p.source_catalogue}|${p.manufacturer_code || ''}|${p.source_brand || ''}`)
      );
      const catalogueOptions = catalogueResults
        .map((c) => ({ ...c, __catalogue: true, id: `__cat_${c.item_id}` }))
        .filter((c) => !linked.has(`${c.catalogue_id}|${c.source_code || ''}|${c.brand || ''}`));
      base = [...(parts || []), ...catalogueOptions];
    }
    if (!jobsActive || jobResults.length === 0) return base;
    const jobOptions = jobResults.map((j) => ({ ...j, __job: true, __raw: j, id: `__job_${j.id}` }));
    return [...base, ...jobOptions];
  }, [catalogueActive, chipsActive, parts, catalogueResults, jobsActive, jobResults]);

  const resetAfterSelect = () => {
    setValue(null);
    setInputValue('');
    setSearchInput('');
    setCatalogueResults([]);
    setJobResults([]);
    // Keep the till flow rolling: focus back for the next scan.
    if (inputRef.current) setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  };

  const finishSelect = (part, meta) => {
    onSelect(part, meta);
    if (clearOnSelect) resetAfterSelect();
    else setValue(part);
  };

  const select = async (option) => {
    if (!option) {
      setValue(null);
      // Form hosts keep the selection visible — tell them it was cleared.
      if (!clearOnSelect) onSelect(null, {});
      return;
    }
    if (option.__job) {
      // Saved job: hand the untouched JobSerializer object to the host — no
      // materialize, no stock match.
      onSelectJob(option.__raw);
      if (clearOnSelect) resetAfterSelect();
      else setValue(option);
      return;
    }
    if (option.__catalogue) {
      // Materialize into a Part (the service reuses an existing linked/matching
      // Part when one exists), then flow through the normal selection path.
      setCatalogueBusy(true);
      try {
        if (stockMatchCheck && inventoryEnabled) {
          // If the workshop already stocks this code, reconcile first — reuse
          // the stocked part and optionally adopt the catalogue prices. The
          // pre-check is best-effort: on failure fall through to a plain
          // materialize so a network blip never blocks adding the part.
          try {
            const smRes = await axios.get(`/api/catalogue-items/${option.item_id}/stock-match/`);
            const { match, catalogue_prices: cataloguePrices } = smRes.data || {};
            const needsReconcile =
              match &&
              match.is_tracked &&
              (match.match_basis === 'code_tracked' || match.cost_differs || match.sell_differs);
            if (needsReconcile) {
              setReconcile({ option, match, cataloguePrices });
              return; // finally clears catalogueBusy; the dialog drives the rest
            }
          } catch {
            /* ignore — proceed with plain materialize below */
          }
        }
        const res = await axios.post(`/api/catalogue-items/${option.item_id}/materialize/`);
        const part = res.data?.part;
        if (part) finishSelect(part, { fromCatalogue: true });
      } catch {
        setSnack({ open: true, message: t('catalogues.materializeFailed'), severity: 'error' });
      } finally {
        setCatalogueBusy(false);
      }
      return;
    }
    finishSelect(option, {});
  };

  const resolveCode = async (code) => {
    const shelfParam = shelfActive ? '&has_inventory=true' : '';
    const codeParam = codeOnly ? '&code_only=true' : '';
    const resp = await axios.get(
      `/api/parts/?search=${encodeURIComponent(code)}&typeahead=true${shelfParam}${codeParam}`
    );
    const results = resp.data.results || resp.data;
    return Array.isArray(results) ? results : [];
  };

  const handleBarcodeScan = async (code) => {
    setScannerOpen(false);
    try {
      const results = await resolveCode(code);
      if (results.length === 1) {
        select(results[0]);
      } else if (results.length === 0) {
        setSnack({ open: true, message: `${t('parts_sales.barcodeNotFound')} (${code})`, severity: 'warning' });
      } else {
        setInputValue(code);
        setSearchInput(code);
        setSnack({ open: true, message: t('parts_sales.barcodeMultiple'), severity: 'info' });
      }
    } catch (e) {
      setSnack({ open: true, message: t('parts_sales.barcodeError'), severity: 'error' });
    }
  };

  const resolveJobs = async (query) => {
    const resp = await axios.get('/api/jobs/', { params: { search: query, typeahead: 'true' } });
    const results = resp.data?.results || resp.data;
    return Array.isArray(results) ? results : [];
  };

  // Scan-gun Enter: MUI already consumed the event (defaultPrevented) when a
  // highlighted option was selected — only handle the "results not in yet" case.
  // With the jobs toggle on, a typed job name + Enter resolves the job too, so
  // the seller never gets "not found" for a job that is one keystroke away.
  const handleEnter = async (e) => {
    if (e.key !== 'Enter' || e.defaultPrevented) return;
    const query = (inputValue || '').trim();
    if (query.length < 3) return;
    e.preventDefault();
    try {
      const [partHits, jobHits] = await Promise.all([
        resolveCode(query),
        jobsActive ? resolveJobs(query) : Promise.resolve([]),
      ]);
      if (partHits.length === 1 && jobHits.length === 0) {
        select(partHits[0]);
      } else if (partHits.length === 0 && jobHits.length === 1) {
        select({ ...jobHits[0], __job: true, __raw: jobHits[0], id: `__job_${jobHits[0].id}` });
      } else if (partHits.length === 0 && jobHits.length === 0) {
        setSnack({
          open: true,
          message: `${t(jobsActive ? 'parts_sales.searchNotFound' : 'parts_sales.barcodeNotFound')} (${query})`,
          severity: 'warning',
        });
      }
      // Several matches: leave the dropdown open for a manual pick.
    } catch {
      /* the debounced search will still populate the dropdown */
    }
  };

  const sellPriceOf = (option) => {
    // Server-resolved walk-in price (includes margin-over-cost parts) when present.
    if (option.pos_prices) {
      if (option.is_margin_vat_scheme) return option.pos_prices.unit_price;
      return orgDefaultVat ? option.pos_prices.unit_price_incl_vat : option.pos_prices.unit_price;
    }
    if (option.is_margin_vat_scheme) return option.sell_price;
    if (orgDefaultVat) return option.sell_price_incl_vat ?? option.sell_price ?? option.price_incl_vat ?? option.price;
    return option.sell_price ?? option.price;
  };

  const getOptionLabel = (o) => {
    if (!o) return '';
    if (o.__job) return o.job_code ? `${o.name} (${o.job_code})` : (o.name || '');
    if (o.__catalogue) return o.name || o.source_code || '';
    const code = o.primary_code || o.manufacturer_code;
    return code ? `${o.name} (${code})` : (o.name || '');
  };

  return (
    <Box>
    {/* Above the field — the results popper opens downward and would cover it there.
        The default FormControlLabel negative margin keeps the checkbox glyph flush
        with the field's left edge; py balances the row's vertical rhythm. Wraps on
        narrow screens so the two scope toggles never push the field sideways. */}
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5, mt: -0.5, mb: 0.25 }}>
      {toggleVisible && !chipsActive && (
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={shelfOnly}
              onChange={(e) => toggleShelf(e.target.checked)}
              inputProps={{ 'data-testid': 'parts-search-shelf-only' }}
              sx={{ py: 0.5 }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t('inventory.onlyTrackedParts')}
            </Typography>
          }
        />
      )}
      {/* disableInteractive: the hints open over the search field below, and an interactive
          tooltip kept swallowing the click into it. */}
      {jobsToggleVisible && (
        <Tooltip title={t('parts_sales.searchJobsHint')} disableInteractive>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={searchJobs}
                onChange={(e) => toggleJobs(e.target.checked)}
                inputProps={{ 'data-testid': 'parts-search-jobs' }}
                sx={{ py: 0.5 }}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                {t('parts_sales.searchJobsToo')}
              </Typography>
            }
          />
        </Tooltip>
      )}
      {/* Unconditional: a workshop that searches by code does so whether or not
          inventory tracking is on and whether or not catalogue chips are active. */}
      <Tooltip title={t('partSearch.codeOnlyHint')} disableInteractive>
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={codeOnly}
              onChange={(e) => setCodeOnly(e.target.checked)}
              inputProps={{ 'data-testid': 'parts-search-code-only' }}
              sx={{ py: 0.5 }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {t('partSearch.codeOnly')}
            </Typography>
          }
        />
      </Tooltip>
      {filtersCollapsible && chipsEnabled && catalogueSources.length > 0 && (
        <Typography
          component="button"
          type="button"
          variant="caption"
          onClick={() => setFiltersOpen((open) => !open)}
          data-testid="parts-search-toggle-filters"
          sx={{
            ml: 'auto',
            background: 'none',
            border: 0,
            p: 0,
            cursor: 'pointer',
            color: 'primary.main',
            fontFamily: 'inherit',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {filtersOpen || chipsActive ? t('partSearch.hideFilters') : t('partSearch.showFilters')}
        </Typography>
      )}
    </Box>
    {/* Restrict the search to specific vendor catalogues (multi-select).
        None selected = search registry + all catalogues. */}
    {chipsVisible && (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1, alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.25 }}>
          {t('catalogues.filterBySource')}
        </Typography>
        {catalogueSources.map((src) => {
          const selected = selectedCatalogueIds.includes(src.id);
          const vendorLabel = src.vendor_name || src.name;
          return (
            <Chip
              key={src.id}
              label={duplicateVendorLabels.has(vendorLabel) ? src.name : vendorLabel}
              onClick={() =>
                setSelectedCatalogueIds((prev) =>
                  prev.includes(src.id) ? prev.filter((x) => x !== src.id) : [...prev, src.id]
                )
              }
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              size="small"
              data-testid={`catalogue-filter-${src.id}`}
            />
          );
        })}
      </Box>
    )}
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Autocomplete
        sx={{ flex: 1 }}
        options={options}
        value={value}
        inputValue={inputValue}
        onChange={(e, v) => select(v)}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={(o, v) => o?.id === v?.id}
        disabled={disabled || catalogueBusy}
        {...autocompleteProps}
        loading={!!autocompleteProps.loading || jobsLoading}
        noOptionsText={t('No options')}
        loadingText={t('Loading...')}
        onOpen={(e) => {
          // Catalogue-filter mode shows a browse preview on open. Otherwise a click
          // (or arrow key) on an empty/short field lists the newest parts, like the
          // work-order AddPartDialog; loaded per open so till stock counts stay
          // current. Opening by typing is skipped: the typed query searches itself.
          if (chipsActive) runCatalogueSearch(inputValue);
          else if (e?.type !== 'change' && (inputValue || '').trim().length < 3) loadInitial();
        }}
        onInputChange={(e, v, reason) => {
          setInputValue(v);
          // When filter chips are active the registry isn't searched.
          if (!chipsActive) autocompleteProps.onInputChange(e, v, reason);
          if (reason === 'input') {
            runCatalogueSearch(v);
            runJobSearch(v);
          }
        }}
        renderOption={(props, option) => {
          if (option.__job) {
            // Saved job. The till bills at the standard VAT rate, so the shown
            // gross is the exact stored gross (entered VAT-inclusive) or
            // price + standard VAT — never Job.vat_rate, which may be stale.
            const rawPrice = option.price != null ? parseFloat(option.price) : null;
            const hasPrice = rawPrice != null && rawPrice > 0;
            const enteredWithVat = !!option.price_entered_with_vat && option.stored_price_incl_vat != null;
            const shownPrice = !hasPrice
              ? null
              : orgDefaultVat
                ? enteredWithVat
                  ? parseFloat(option.stored_price_incl_vat)
                  : calcPriceInclVat(rawPrice, DEFAULT_VAT_RATE)
                : rawPrice;
            const hourly = option.price_type === 'hourly';
            const subline = [option.category_name, hourly ? option.price_type_display : null].filter(Boolean).join(' · ');
            return (
              <Box component="li" {...props} key={option.id} data-testid={`job-option-${option.__raw.id}`}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Chip
                        label={t('parts_sales.jobTag')}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '10px' }}
                      />
                      {option.name}
                      {option.job_code && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ...codeFont }}>
                          {option.job_code}
                        </Typography>
                      )}
                    </Typography>
                    {subline && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {subline}
                      </Typography>
                    )}
                  </Box>
                  {shownPrice != null && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, ...numFont }}>
                        {formatPriceWithCurrency(shownPrice)}
                        {hourly ? `/${t('parts_sales.hourUnit')}` : ''}
                        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                          {t(orgDefaultVat ? 'incl. VAT' : 'excl. VAT')}
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }
          if (option.__catalogue) {
            // Catalogue prices are stored ex-VAT; follow the org display setting.
            // Sell-price-only feeds carry no cost — show their OVH instead.
            const hasCost = option.purchase_price != null;
            const rawPrice = hasCost ? option.purchase_price : option.list_price;
            const shownPrice = rawPrice == null ? null : orgDefaultVat ? calcPriceInclVat(rawPrice) : rawPrice;
            return (
              <Box component="li" {...props} key={option.id} data-testid={`catalogue-option-${option.item_id}`}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Chip
                        label={t('catalogues.priceList')}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '10px' }}
                      />
                      {option.name || option.source_code}
                      {option.source_code && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ...codeFont }}>
                          {option.source_code}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {[option.brand, option.catalogue_name].filter(Boolean).join(' · ')}
                    </Typography>
                  </Box>
                  {shownPrice != null && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, ...numFont }}>
                        {formatPriceWithCurrency(parseFloat(shownPrice))}
                        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                          {t(orgDefaultVat ? 'incl. VAT' : 'excl. VAT')}
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '10px' }}>
                        {hasCost ? t('catalogues.purchasePrice') : t('catalogues.ovhLabel')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }
          const stockInfo = option.stock_info;
          const stockQty = stockInfo ? parseFloat(stockInfo.quantity_free) : null;
          const sellPrice = sellPriceOf(option);
          // Margin-scheme prices are always gross; otherwise follow the org display setting.
          const priceInclVat = option.is_margin_vat_scheme || orgDefaultVat;
          return (
            <Box component="li" {...props} key={option.id}>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {option.name}
                    {(option.primary_code || option.manufacturer_code) && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, ...codeFont }}>
                        {option.primary_code || option.manufacturer_code}
                      </Typography>
                    )}
                    {option.unit && option.unit !== 'kpl' && (
                      <Chip
                        label={option.unit}
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1, height: 18, fontSize: '10px' }}
                        data-testid="part-option-unit"
                      />
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                    {[option.manufacturer_name, option.vendor_name].filter(Boolean).join(' · ')}
                    {option.is_margin_vat_scheme ? ` · ${t('parts_sales.marginTag')}` : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                  {sellPrice != null && (
                    <Typography variant="body2" sx={{ fontWeight: 600, ...numFont }}>
                      {formatPriceWithCurrency(parseFloat(sellPrice))}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                        {t(priceInclVat ? 'incl. VAT' : 'excl. VAT')}
                      </Typography>
                    </Typography>
                  )}
                  {inventoryEnabled && stockInfo && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatQuantity(stockInfo.quantity_free)} ${t('inventory.pcsShort')}`}
                        color={
                          stockInfo.is_negative ? 'error'
                            : stockInfo.is_low_stock ? 'warning'
                            : stockQty > 0 ? 'success' : 'default'
                        }
                        sx={{ height: 20, fontSize: '11px' }}
                      />
                      {stockInfo.shelf_location && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                          {stockInfo.shelf_location}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={(node) => {
              inputRef.current = node;
              if (typeof params.inputRef === 'function') params.inputRef(node);
              else if (params.inputRef) params.inputRef.current = node;
            }}
            label={label}
            placeholder={placeholder}
            size={big ? 'medium' : size}
            autoFocus={autoFocus}
            onKeyDown={handleEnter}
            inputProps={{ ...params.inputProps, 'data-testid': inputTestId }}
            InputProps={
              big
                ? {
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <QrCodeScannerIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.disabled' }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }
                : params.InputProps
            }
            sx={
              big
                ? {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      fontSize: '1.05rem',
                      backgroundColor: 'background.paper',
                    },
                  }
                : undefined
            }
          />
        )}
      />
      {scannerEnabled && (
        <Tooltip title={t('parts_sales.scan')}>
          <IconButton onClick={() => setScannerOpen(true)} color="primary" data-testid="parts-sales-scan">
            <QrCodeScannerIcon />
          </IconButton>
        </Tooltip>
      )}
      <BarcodeScannerDialog open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleBarcodeScan} />
      {stockMatchCheck && (
        <CatalogueStockReconcileDialog
          open={!!reconcile}
          option={reconcile?.option}
          match={reconcile?.match}
          cataloguePrices={reconcile?.cataloguePrices}
          onResolved={(part) => {
            setReconcile(null);
            finishSelect(part, { fromCatalogue: true });
          }}
          onClose={() => setReconcile(null)}
        />
      )}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
}

export default PartAutocomplete;
